// src/components/shared/icons.tsx — universal NU icon library.
//
// Ported VERBATIM from NU-DATA-DASHBOARD/kits/_core/icons.jsx plus the
// nu-data delta (kits/nu-data/components/icons.jsx). These are hand-rolled
// single-stroke 24×24 Lucide-STYLE SVGs (stroke="currentColor",
// strokeWidth=1.6) — they are NOT the `lucide-react` package, and must not be
// swapped for it: the golden screenshots in the design pack were rendered
// against these exact path strings, so pixel fidelity depends on them.
//
// The kit loads _core/icons.jsx first and then Object.assign()s the nu-data
// delta over it, so the delta versions of `groups` / `export` / `feedback`
// win at runtime. The merge below reproduces that order exactly.

/* eslint no-unused-vars: ["error", { "args": "none" }] --
   the base rule (no type-aware variant configured in this repo) false-flags
   parameter names inside TypeScript function-type annotations as unused. */

import type { CSSProperties, ReactElement } from "react";

export interface NuIconProps {
  /** Rendered width/height in px. Kit default: 24. */
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** Accessible name. Icons are decorative (aria-hidden) unless a title is given. */
  title?: string;
}

export type NuIconComponent = (props: NuIconProps) => ReactElement;

interface IconOptions {
  strokeWidth?: number;
  viewBox?: string;
  fill?: string;
}

/** SVG icon factory — identical geometry contract to the kit's ICON(). */
export function ICON(
  d: string | string[],
  opts: IconOptions = {},
): NuIconComponent {
  const sw = opts.strokeWidth || 1.6;
  const vb = opts.viewBox || "0 0 24 24";
  const fill = opts.fill || "none";
  return function Icon({
    size = 24,
    className = "",
    style,
    title,
  }: NuIconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox={vb}
        fill={fill}
        stroke="currentColor"
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        style={style}
        role={title ? "img" : undefined}
        aria-label={title}
        aria-hidden={title ? undefined : true}
        focusable="false"
      >
        {Array.isArray(d) ? (
          d.map((p, i) => <path key={i} d={p} />)
        ) : (
          <path d={d} />
        )}
      </svg>
    );
  };
}

/* Universal set — kits/_core/icons.jsx, verbatim path data. */
const coreIcons = {
  /* Nav + dashboards */
  dashboard: ICON(["M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z"]),
  search: ICON(["M21 21l-4.3-4.3", "M11 3a8 8 0 1 1 0 16 8 8 0 0 1 0-16z"]),
  user: ICON([
    "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2",
    "M12 7a4 4 0 1 1 0 8 4 4 0 0 1 0-8z",
  ]),
  phone: ICON(
    "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z",
  ),
  report: ICON([
    "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z",
    "M14 2v6h6",
    "M16 13H8",
    "M16 17H8",
  ]),
  list: ICON([
    "m9 11 3 3 8-8",
    "M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h11",
  ]),
  settings: ICON([
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
    "M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3h0a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 1 1.5h0a1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8v0a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z",
  ]),
  help: ICON([
    "M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3",
    "M12 17h.01",
    "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z",
  ]),

  /* CRUD + actions */
  plus: ICON("M12 5v14M5 12h14"),
  edit: ICON([
    "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7",
    "M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z",
  ]),
  trash: ICON([
    "M3 6h18",
    "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6",
    "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
  ]),
  copy: ICON([
    "M20 9h-9a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2z",
    "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1",
  ]),
  close: ICON("M18 6 6 18M6 6l12 12"),
  refresh: ICON([
    "M23 4v6h-6",
    "M1 20v-6h6",
    "M3.51 9a9 9 0 0 1 14.85-3.36L23 10",
    "M1 14l4.64 4.36A9 9 0 0 0 20.49 15",
  ]),
  download: ICON([
    "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4",
    "M7 10l5 5 5-5",
    "M12 15V3",
  ]),
  upload: ICON([
    "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4",
    "M17 8l-5-5-5 5",
    "M12 3v12",
  ]),
  filter: ICON("M22 3H2l8 9.46V19l4 2v-8.54L22 3z"),

  /* Status + meta */
  tick: ICON("m5 13 4 4L19 7", { strokeWidth: 2.5 }),
  warning: ICON([
    "M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z",
    "M12 9v4",
    "M12 17h.01",
  ]),
  ban: ICON([
    "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z",
    "M4.93 4.93l14.14 14.14",
  ]),
  info: ICON([
    "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z",
    "M12 16v-4",
    "M12 8h.01",
  ]),
  bell: ICON([
    "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9",
    "M10.3 21a1.94 1.94 0 0 0 3.4 0",
  ]),
  calendar: ICON([
    "M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z",
    "M16 2v4M8 2v4M3 10h18",
  ]),
  clock: ICON([
    "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z",
    "M12 6v6l4 2",
  ]),
  shield: ICON("M9 12l2 2 4-4M21 12c0 5-9 9-9 9s-9-4-9-9V5l9-3 9 3z"),
  globe: ICON([
    "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z",
    "M2 12h20",
    "M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z",
  ]),

  /* Chevrons + arrows */
  chevDown: ICON("m6 9 6 6 6-6"),
  chevRight: ICON("m9 18 6-6-6-6"),
  chevLeft: ICON("m15 18-6-6 6-6"),
  chevUpDown: ICON(["m7 15 5 5 5-5", "m7 9 5-5 5 5"]),
  arrowRight: ICON(["M5 12h14", "m13 5 7 7-7 7"]),
  more: ICON([
    "M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
    "M19 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
    "M5 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
  ]),

  /* Generic productivity */
  link: ICON([
    "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71",
    "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
  ]),

  /* Comms + collaboration */
  chat: ICON(
    "M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z",
  ),
  feedback: ICON([
    "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
    "M8 9h8",
    "M8 13h6",
  ]),
  send: ICON(["M22 2 11 13", "m22 2-7 20-4-9-9-4 20-7z"]),
  activity: ICON("M22 12h-4l-3 9L9 3l-3 9H2"),
  groups: ICON([
    "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
    "M9 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z",
    "M23 21v-2a4 4 0 0 0-3-3.87",
    "M16 3.13a4 4 0 0 1 0 7.75",
  ]),
  database: ICON([
    "M21 5c0 1.66-4.03 3-9 3S3 6.66 3 5s4.03-3 9-3 9 1.34 9 3z",
    "M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5",
    "M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3",
  ]),
  export: ICON([
    "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4",
    "m17 8-5-5-5 5",
    "M12 3v12",
  ]),
  api: ICON(["m16 18 6-6-6-6", "m8 6-6 6 6 6", "m14.5 4-5 16"]),
} satisfies Record<string, NuIconComponent>;

/* NU-DATA delta — kits/nu-data/components/icons.jsx, verbatim path data.
 * Applied AFTER the core set, exactly as the kit's Object.assign() does, so
 * the delta versions of groups/export/feedback override the universal ones. */
const nuDataIconDelta = {
  enrich: ICON([
    "M12 2v6m0 4v10M5 5l4 4m6 6l4 4M2 12h6m4 0h10M5 19l4-4m6-6l4-4",
  ]),
  groups: ICON([
    "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
    "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
    "M23 21v-2a4 4 0 0 0-3-3.87",
    "M16 3.13a4 4 0 0 1 0 7.75",
  ]),
  connections: ICON(["M16 16l-4-4 4-4M8 8l4 4-4 4", "M2 12h20"]),
  export: ICON([
    "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4",
    "M7 10l5 5 5-5",
    "M12 15V3",
  ]),
  feedback: ICON([
    "M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z",
  ]),
  clipboard: ICON([
    "M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2",
    "M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z",
    "M9 14l2 2 4-4",
  ]),
  card: ICON([
    "M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z",
    "M1 10h22",
  ]),
  key: ICON([
    "M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0L17 9l2-2-1.5-1.5M15.5 7.5L19 4",
  ]),
} satisfies Record<string, NuIconComponent>;

export const NuIcons = { ...coreIcons, ...nuDataIconDelta };

export type NuIconName = keyof typeof NuIcons;
